import { createSelector, unwrapResult } from "@reduxjs/toolkit";
import React, { memo, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { UiCenter, UiDropdown, UiDropdownProps, UiText } from "~/components/elements";
import { OPTION_ALL, useDebounce, useDropdown, useIsDirty } from "~/hooks";
import { BaseOption, UseDropdownProps } from "~/hooks/useDropdown";
import { RootState } from "~/redux/root-reducer";
import { AppDispatch } from "~/redux/root-store";
import { DropdownOption } from "~/types/general";
import isArray from "lodash/isArray";
import isEmpty from "lodash/isEmpty";
import { Spinner } from "@chakra-ui/react";

export interface BaseDropdownProps<ItemType> extends UiDropdownProps, UseDropdownProps<ItemType> {
  name: string;
  getDataSelector?: (state: RootState) => any;
  onSearchAction?: (query?: string) => any;
  isFetchOneTimeOnly?: boolean;
  defaultOption?: DropdownOption;
  defaultValue?: any;
  isHidden?: boolean;
}

interface CacheProps {
  isFetching?: boolean;
  didFetched?: boolean;
}
const cacheMap: Record<string, CacheProps> = {};

const findDefaultValues = (
  defaultOption: DropdownOption | undefined,
  formattedOptions: { label: string; value: string; icon?: React.ReactNode }[],
  defaultValue: any | any[],
  includeAll: undefined | boolean,
  multiple: undefined | boolean
) => {
  if (defaultOption) {
    return defaultOption;
  }

  if (typeof defaultValue === "undefined") {
    return undefined;
  }

  if (!multiple) {
    return formattedOptions.find(o => o.value === defaultValue) || (includeAll ? OPTION_ALL : undefined);
  }

  if (!isArray(defaultValue)) {
    defaultValue = [defaultValue];
  }
  const res: DropdownOption[] = [];
  defaultValue.forEach((v: any) => {
    const option = formattedOptions.find(o => o.value === v);
    if (option) {
      res.push(option);
    }
  });
  return res;
};

function BaseDropdown<ItemType extends BaseOption>({
  name,
  getDataSelector,
  onSearchAction,
  isFetchOneTimeOnly = false,
  onSelect,
  defaultValue,
  defaultOption,
  getLabel,
  getValue,
  includeAll = false,
  multiple = false,
  placeholder = "...",
  isHidden = false,
  options,
  ...rest
}: BaseDropdownProps<ItemType>) {
  const dispatch: AppDispatch = useDispatch();
  let data = useSelector(getDataSelector || createSelector(() => []));
  data = data?.data || data; // Sometimes our selector wrap data in a `data` key or may be not

  const {
    searchQuery,
    options: formattedOptions,
    onInputChange,
    onChange,
  } = useDropdown<ItemType>({
    defaultValue,
    multiple,
    includeAll,
    onSelect,
    getLabel,
    getValue,
    optionData: options || data,
  });
  const isOptionsReady = useIsDirty(formattedOptions) || !isEmpty(formattedOptions);

  const query = useDebounce(searchQuery, 300);

  useEffect(() => {
    if (onSearchAction && !isFetchOneTimeOnly) {
      const fetchData = async () => {
        await dispatch(onSearchAction(query));
      };

      fetchData();
    }
  }, [query, dispatch, isFetchOneTimeOnly, onSearchAction]);

  useEffect(() => {
    if (onSearchAction && isFetchOneTimeOnly && !cacheMap[name]?.didFetched && !cacheMap[name]?.isFetching) {
      cacheMap[name] = {};
      const fetchData = async () => {
        cacheMap[name].isFetching = true;
        try {
          const resAction = await dispatch(onSearchAction());
          const res = unwrapResult(resAction);
          if (res.source) {
            // Request ok
            cacheMap[name].didFetched = true;
          }
        } catch (e) {
          // eslint-disable-next-line no-console
          console.log(e);
        } finally {
          cacheMap[name].isFetching = false;
        }
      };

      fetchData();
    }
  }, [dispatch, onSearchAction, isFetchOneTimeOnly, name]);

  const defaultVal = findDefaultValues(defaultOption, formattedOptions, defaultValue, includeAll, multiple);

  if (isHidden) {
    return null;
  }
  if (!isOptionsReady) {
    return (
      <UiCenter w="100px">
        <Spinner size="md" />
      </UiCenter>
    );
  }

  return (
    <UiDropdown
      defaultValue={defaultVal}
      options={formattedOptions}
      onInputChange={onInputChange}
      // @ts-ignore
      onChange={onChange}
      placeholder={placeholder}
      isClearable
      multiple={multiple}
      // eslint-disable-next-line react/no-unstable-nested-components
      noOptionsMessage={() => <UiText>No data</UiText>}
      {...rest}
    />
  );
}

export default memo(BaseDropdown);
